import * as React from 'react';
import { Client } from '../../types/client';
import { Button, Typography, Checkbox, Box, IconButton, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import AddressAutocomplete from '../common/AddressAutocomplete';
import { EmailField, PhoneField, ThemedTextField } from '../common/ValidatedFields';

interface Lookup {
  _id: string;
  category: string;
  value: string;
  displayName: string;
  active: boolean;
  sessionRate?: number;
  numberOfSessions?: number;
}

interface EmergencyContact {
  name: string;
  phone: string;
  relationship: string;
}

interface PersonalInformationProps {
  client: Client;
  lookups: {
    gender: Lookup[];
    availability: Lookup[];
    sessionTypes: Lookup[];
  };
  onUpdate: (updates: Partial<Client>) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  address: string;
  setAddress: (value: string) => void;
  street: string;
  setStreet: (value: string) => void;
  addressLine2: string;
  setAddressLine2: (value: string) => void;
  addressLine3: string;
  setAddressLine3: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  postcode: string;
  setPostcode: (value: string) => void;
  county: string;
  setCounty: (value: string) => void;
  country: string;
  setCountry: (value: string) => void;
  emergencyContactName: string;
  setEmergencyContactName: (value: string) => void;
  emergencyContactPhone: string;
  setEmergencyContactPhone: (value: string) => void;
  emergencyContactRelationship: string;
  setEmergencyContactRelationship: (value: string) => void;
  emergencyContacts: EmergencyContact[];
  setEmergencyContacts: (value: EmergencyContact[]) => void;
  gpPractice: string;
  setGpPractice: (value: string) => void;
  gpAddress: string;
  setGpAddress: (value: string) => void;
  gpAddressLine2: string;
  setGpAddressLine2: (value: string) => void;
  gpAddressLine3: string;
  setGpAddressLine3: (value: string) => void;
  gpCity: string;
  setGpCity: (value: string) => void;
  gpCounty: string;
  setGpCounty: (value: string) => void;
  gpPostcode: string;
  setGpPostcode: (value: string) => void;
  gpCountry: string;
  setGpCountry: (value: string) => void;
  gpPhone: string;
  setGpPhone: (value: string) => void;
  gender: string;
  setGender: (value: string) => void;
  selectedAvailability: string[];
  setSelectedAvailability: (value: string[]) => void;
  availabilitySlots: Array<{ day: string; timeRanges: Array<{ start: string; end: string }> }>;
  setAvailabilitySlots: (value: Array<{ day: string; timeRanges: Array<{ start: string; end: string }> }>) => void;
  selectedSessionTypes: string[];
  setSelectedSessionTypes: (value: string[]) => void;
}

const PersonalInformation: React.FC<PersonalInformationProps> = ({
  client,
  lookups,
  onUpdate,
  isEditing,
  setIsEditing,
  address,
  setAddress,
  street,
  setStreet,
  addressLine2,
  setAddressLine2,
  addressLine3,
  setAddressLine3,
  city,
  setCity,
  postcode,
  setPostcode,
  county,
  setCounty,
  country,
  setCountry,
  emergencyContactName,
  setEmergencyContactName,
  emergencyContactPhone,
  setEmergencyContactPhone,
  emergencyContactRelationship,
  setEmergencyContactRelationship,
  emergencyContacts,
  setEmergencyContacts,
  gpPractice,
  setGpPractice,
  gpAddress,
  setGpAddress,
  gpAddressLine2,
  setGpAddressLine2,
  gpAddressLine3,
  setGpAddressLine3,
  gpCity,
  setGpCity,
  gpCounty,
  setGpCounty,
  gpPostcode,
  setGpPostcode,
  gpCountry,
  setGpCountry,
  gpPhone,
  setGpPhone,
  gender,
  setGender,
  selectedAvailability,
  setSelectedAvailability,
  selectedSessionTypes,
  setSelectedSessionTypes,
}) => {
  const [firstName, setFirstName] = React.useState(client.firstName || '');
  const [lastName, setLastName] = React.useState(client.lastName || '');
  const [email, setEmail] = React.useState(client.email || '');
  const [phone, setPhone] = React.useState(client.phone || '');
  
  const [day, setDay] = React.useState('');
  const [month, setMonth] = React.useState('');
  const [year, setYear] = React.useState('');
  const [calculatedAge, setCalculatedAge] = React.useState<number | null>(null);

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const years = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  );

  const genderOptions = lookups.gender;

  const getGenderDisplayName = React.useCallback((value: string) => {
    const option = genderOptions.find(opt => opt.value === value);
    return option ? option.displayName : value;
  }, [genderOptions]);

  const calculateAge = (dob: string) => {
    const birthDate = new Date(dob);
    const age = new Date().getFullYear() - birthDate.getFullYear();
    return age;
  };

  React.useEffect(() => {
    if (client.dob) {
      const date = new Date(client.dob);
      setDay(String(date.getDate()).padStart(2, '0'));
      setMonth(String(date.getMonth() + 1).padStart(2, '0'));
      setYear(String(date.getFullYear()));
      setCalculatedAge(calculateAge(client.dob));
    }
  }, [client.dob]);

  React.useEffect(() => {
    if (day && month && year) {
      const dateStr = `${year}-${month}-${day}`;
      const age = calculateAge(dateStr);
      setCalculatedAge(age);
    }
  }, [day, month, year]);

  const handleSaveClick = (): void => {
    let dobString: string | undefined = undefined;
    if (day && month && year) {
      dobString = `${year}-${month}-${day}`;
    }

    onUpdate({
      firstName,
      lastName,
      email,
      phone,
      dob: dobString,
      address,
      street,
      city,
      postcode,
      emergencyContactName,
      emergencyContactPhone,
      emergencyContactRelationship,
      emergencyContacts,
      gpPractice,
      gpAddress,
      gpAddressLine2,
      gpAddressLine3,
      gpCity,
      gpCounty,
      gpPostcode,
      gpCountry,
      gpPhone,
      gender,
      availability: selectedAvailability,
      sessionTypes: selectedSessionTypes
    });
    setIsEditing(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    setter: (value: string) => void
  ): void => {
    setter(e.target.value);
  };

  const handleEditClick = (): void => {
    setIsEditing(true);
  };
  const handleCancelClick = (): void => {
    setIsEditing(false);
  };

  const renderSessionTypes = () => {
    return (
    <div>
      <label className="block text-sm font-bold theme-text-primary mb-4">Session Types</label>
      <div className="glass-card p-3 sm:p-4 rounded-lg">
        {isEditing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {lookups.sessionTypes.map((option) => {
              return (
                <div key={option._id} className="flex items-center">
                  <Checkbox
                    checked={selectedSessionTypes?.includes(option._id) || false}
                    onChange={() => {
                      const newTypes = selectedSessionTypes?.includes(option._id)
                        ? selectedSessionTypes.filter(v => v !== option._id)
                        : [...(selectedSessionTypes || []), option._id];
                      setSelectedSessionTypes(newTypes);
                    }}
                  />
                  <span className="ml-2 text-sm theme-text-primary">{option.displayName}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {client.sessionTypes?.map((typeValue) => {
              // Try matching by _id first (correct format), then by value (legacy format)
              const option = lookups.sessionTypes.find(opt => opt._id === typeValue || opt.value === typeValue);
              return (
                <div key={typeValue} className="flex items-center">
                  <span className="text-sm theme-text-primary">
                    {option ? option.displayName : 'Unknown Session Type'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
    );
  };

  React.useEffect(() => {
    if (client) {
      setAddress(client.address || '');
      setStreet(client.street || '');
      setAddressLine2(client.addressLine2 || '');
      setAddressLine3(client.addressLine3 || '');
      setCity(client.city || '');
      setPostcode(client.postcode || '');
      setCounty(client.county || '');
      setCountry(client.country || '');
      setEmergencyContactName(client.emergencyContactName || '');
      setEmergencyContactPhone(client.emergencyContactPhone || '');
      setEmergencyContactRelationship(client.emergencyContactRelationship || '');
      // Handle emergency contacts array with backward compatibility
      if (client.emergencyContacts && client.emergencyContacts.length > 0) {
        setEmergencyContacts(client.emergencyContacts);
      } else if (client.emergencyContactName || client.emergencyContactPhone || client.emergencyContactRelationship) {
        setEmergencyContacts([{
          name: client.emergencyContactName || '',
          phone: client.emergencyContactPhone || '',
          relationship: client.emergencyContactRelationship || ''
        }]);
      } else {
        setEmergencyContacts([]);
      }
      setGpPractice(client.gpPractice || '');
      setGpAddress(client.gpAddress || '');
      setGpPhone(client.gpPhone || '');
      setGender(client.gender || '');
      setSelectedAvailability(client.availability || []);
      setSelectedSessionTypes(client.sessionTypes || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="glass-card-light rounded-lg p-6">
        {/* Header with Edit Icon */}
        <div className="flex justify-between items-center mb-6">
          <Typography 
            variant="h4" 
            component="h2"
            className="theme-text-primary" 
            gutterBottom
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '1.8rem',
              letterSpacing: '0.5px',
              marginBottom: 0
            }}
          >
            Basic Information
          </Typography>
          {!isEditing ? (
            <IconButton 
              onClick={handleEditClick}
              size="small" 
              sx={{ 
                color: '#667eea', 
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.2)'
                }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleCancelClick}
                size="small"
                sx={{
                  textTransform: 'none',
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveClick}
                size="small"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#667eea',
                  '&:hover': {
                    backgroundColor: '#5568d3'
                  }
                }}
              >
                Save
              </Button>
            </Box>
          )}
        </div>

        {/* Grid Layout for Responsive Fields */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4">
          {/* First Name */}
          <div>
            {isEditing ? (
              <ThemedTextField
                value={firstName}
                onChange={setFirstName}
                label="First Name"
                required
              />
            ) : (
              <>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
                  First Name
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 400, fontSize: '1rem' }}>
                  {firstName || 'Not provided'}
                </Typography>
              </>
            )}
          </div>

          {/* Last Name */}
          <div>
            {isEditing ? (
              <ThemedTextField
                value={lastName}
                onChange={setLastName}
                label="Last Name"
                required
              />
            ) : (
              <>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
                  Last Name
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 400, fontSize: '1rem' }}>
                  {lastName || 'Not provided'}
                </Typography>
              </>
            )}
          </div>

          {/* Email */}
          <div>
            {isEditing ? (
              <EmailField
                value={email}
                onChange={setEmail}
                label="Email"
                required
              />
            ) : (
              <>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
                  Email
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 400, fontSize: '1rem', wordBreak: 'break-all' }}>
                  {email || 'Not provided'}
                </Typography>
              </>
            )}
          </div>

          {/* Phone */}
          <div>
            {isEditing ? (
              <PhoneField
                value={phone}
                onChange={setPhone}
                label="Phone"
                required
              />
            ) : (
              <>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 400, fontSize: '1rem' }}>
                  {phone || 'Not provided'}
                </Typography>
              </>
            )}
          </div>

          {/* Date of Birth */}
          <div>
            {isEditing ? (
              <>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 1, fontSize: '0.75rem', fontWeight: 400 }}>
                  Date of Birth
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl sx={{ flex: 1 }}>
                    <Select
                      value={day}
                      onChange={(e) => setDay(e.target.value)}
                      displayEmpty
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        '& .MuiSelect-select': { color: 'white', padding: '8px 14px', fontSize: '0.875rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                        '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#1e293b',
                            maxHeight: 300,
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              fontSize: '0.875rem',
                              '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' },
                              '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.2)', '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.3)' } }
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem value=""><em>Day</em></MenuItem>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <MenuItem key={d} value={String(d).padStart(2, '0')}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <Select
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      displayEmpty
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        '& .MuiSelect-select': { color: 'white', padding: '8px 14px', fontSize: '0.875rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                        '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#1e293b',
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              fontSize: '0.875rem',
                              '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' },
                              '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.2)', '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.3)' } }
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem value=""><em>Month</em></MenuItem>
                      {months.map(m => (
                        <MenuItem key={m.value} value={m.value}>{m.label.substring(0, 3)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <Select
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      displayEmpty
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        '& .MuiSelect-select': { color: 'white', padding: '8px 14px', fontSize: '0.875rem' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                        '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#1e293b',
                            maxHeight: 300,
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              fontSize: '0.875rem',
                              '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' },
                              '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.2)', '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.3)' } }
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem value=""><em>Year</em></MenuItem>
                      {years.map(y => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
                  Date of Birth
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 400, fontSize: '1rem' }}>
                  {client.dob ? new Date(client.dob).toLocaleDateString('en-GB') : 'Not provided'}
                </Typography>
              </>
            )}
          </div>

          {/* Age */}
          <div>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
              Age
            </Typography>
            <Typography variant="body1" sx={{ color: 'white', fontWeight: 400, fontSize: '1rem' }}>
              {calculatedAge || (client.dob ? calculateAge(client.dob) : 'N/A')}
            </Typography>
          </div>

          {/* Gender */}
          <div>
            {isEditing ? (
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Gender</InputLabel>
                <Select
                  value={gender || ''}
                  onChange={(e) => setGender(e.target.value)}
                  label="Gender"
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '4px',
                    },
                    '& .MuiSelect-select': {
                      color: 'white',
                      padding: '12px 14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1e293b',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' },
                          '&.Mui-selected': { 
                            bgcolor: 'rgba(99, 102, 241, 0.2)',
                            '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.3)' }
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="">Select Gender</MenuItem>
                  {genderOptions.map((option) => (
                    <MenuItem key={option._id} value={option.value}>
                      {option.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
                  Gender
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 400, fontSize: '1rem' }}>
                  {getGenderDisplayName(gender) || 'Not specified'}
                </Typography>
              </>
            )}
          </div>
            {isEditing ? (
              <div className="col-span-2">
                <AddressAutocomplete
                  value={{
                    street: street || '',
                    line2: addressLine2 || '',
                    line3: addressLine3 || '',
                    city: city || '',
                    postcode: postcode || '',
                    county: county || '',
                    country: country || ''
                  }}
                  onChange={(address) => {
                    setStreet(address.street || '');
                    setAddressLine2(address.line2 || '');
                    setAddressLine3(address.line3 || '');
                    setCity(address.city || '');
                    setPostcode(address.postcode || '');
                    setCounty(address.county || '');
                    setCountry(address.country || '');
                  }}
                  fieldMapping={{
                    line1: 'street',
                    line2: 'line2',
                    line3: 'line3',
                    city: 'city',
                    postcode: 'postcode',
                    county: 'county',
                    country: 'country'
                  }}
                  required={false}
                  label="Client Address"
                />
              </div>
            ) : (
              <>
                <div className="col-span-2">
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
                    Street Address
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', fontWeight: 400, fontSize: '1rem' }}>
                    {street || 'Not provided'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
                    City
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', fontWeight: 400, fontSize: '1rem' }}>
                    {city || 'Not provided'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 400 }}>
                    Postcode
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', fontWeight: 400, fontSize: '1rem' }}>
                    {postcode || 'Not provided'}
                  </Typography>
                </div>
              </>
            )}
        </div>
      </div>

      {/* Session Preferences - Only show after assessment */}
      {client.assessed && (
        <div className="glass-card-light rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
          <Typography 
            variant="h4" 
            component="h2"
            className="theme-text-primary" 
            gutterBottom
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '1.8rem',
              letterSpacing: '0.5px',
              marginBottom: 0
            }}
          >
            Session Preferences
          </Typography>
          </div>
          <div className="space-y-6">
            {renderSessionTypes()}
            <div>
              <label className="block text-sm font-bold theme-text-primary mb-4">Availability Options</label>
              <div className="glass-card p-3 sm:p-4 rounded-lg">
                {isEditing ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {lookups.availability
                      .map((option) => (
                        <div key={option._id} className="flex items-center">
                          <Checkbox
                            checked={selectedAvailability?.includes(option._id) || false}
                            onChange={() => {
                              const newAvailability = selectedAvailability?.includes(option._id)
                                ? selectedAvailability.filter(v => v !== option._id)
                                : [...(selectedAvailability || []), option._id];
                              setSelectedAvailability(newAvailability);
                            }}
                          />
                          <span className="ml-2 text-sm theme-text-primary">{option.displayName}</span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedAvailability && selectedAvailability.length > 0 ? (
                      selectedAvailability.map(value => {
                        // Try matching by _id first (correct format), then by value (legacy format)
                        const option = lookups.availability.find(l => l._id === value || l.value === value);
                        return (
                          <div key={value} className="text-sm theme-text-primary">
                            {option?.displayName || 'Unknown Availability'}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-sm theme-text-secondary">No availability options selected</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Emergency Contacts */}
      <div className="glass-card-light rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
        <Typography 
            variant="h4" 
            component="h2"
            className="theme-text-primary" 
            gutterBottom
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '1.8rem',
              letterSpacing: '0.5px',
              marginBottom: 0
            }}
          >
            Emergency Contacts
          </Typography>
          {isEditing && (
            <Button
              variant="contained"
              onClick={() => setEmergencyContacts([...emergencyContacts, { name: '', phone: '', relationship: '' }])}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
                }
              }}
            >
              + Add Contact
            </Button>
          )}
        </div>
        {emergencyContacts.length === 0 ? (
          <div className="glass-card p-3 sm:p-4 md:p-6 rounded-lg text-center">
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              No emergency contacts added
            </Typography>
          </div>
        ) : (
          <div className="space-y-4">
            {emergencyContacts.map((contact, index) => (
              <div key={index} className="glass-card p-3 sm:p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)' }}>
                    Contact {index + 1}
                  </Typography>
                  {isEditing && emergencyContacts.length > 1 && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => setEmergencyContacts(emergencyContacts.filter((_, i) => i !== index))}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: 'auto',
                        px: 2
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                  <div>
                    {isEditing ? (
                      <ThemedTextField
                        value={contact.name}
                        onChange={(value) => {
                          const newContacts = [...emergencyContacts];
                          newContacts[index].name = value;
                          setEmergencyContacts(newContacts);
                        }}
                        label="Name"
                        placeholder="Full name"
                        required
                      />
                    ) : (
                      <>
                        <label className="block text-sm font-bold theme-text-primary mb-2">Name</label>
                        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                          {contact.name || 'Not provided'}
                        </Typography>
                      </>
                    )}
                  </div>
                  <div>
                    {isEditing ? (
                      <PhoneField
                        value={contact.phone}
                        onChange={(value) => {
                          const newContacts = [...emergencyContacts];
                          newContacts[index].phone = value;
                          setEmergencyContacts(newContacts);
                        }}
                        label="Phone"
                        required
                      />
                    ) : (
                      contact.phone ? (
                        <>
                          <label className="block text-sm font-bold theme-text-primary mb-2">Phone</label>
                          <a href={`tel:${contact.phone}`} style={{ textDecoration: 'none' }}>
                            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500, '&:hover': { textDecoration: 'underline', color: 'rgba(255, 255, 255, 1)' } }}>
                              {contact.phone}
                            </Typography>
                          </a>
                        </>
                      ) : (
                        <>
                          <label className="block text-sm font-bold theme-text-primary mb-2">Phone</label>
                          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                            Not provided
                          </Typography>
                        </>
                      )
                    )}
                  </div>
                  <div>
                    {isEditing ? (
                      <ThemedTextField
                        value={contact.relationship}
                        onChange={(value) => {
                          const newContacts = [...emergencyContacts];
                          newContacts[index].relationship = value;
                          setEmergencyContacts(newContacts);
                        }}
                        label="Relationship"
                        placeholder="e.g., Mother, Father"
                        required
                      />
                    ) : (
                      <>
                        <label className="block text-sm font-bold theme-text-primary mb-2">Relationship</label>
                        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                          {contact.relationship || 'Not provided'}
                        </Typography>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* GP Information */}
      <div className="glass-card-light rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
        <Typography 
            variant="h4" 
            component="h2"
            className="theme-text-primary" 
            gutterBottom
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '1.8rem',
              letterSpacing: '0.5px',
              marginBottom: 0
            }}
          >
            GP Information
          </Typography>
        </div>
        <div className="glass-card p-3 sm:p-4 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            {isEditing ? (
              <ThemedTextField
                value={gpPractice}
                onChange={setGpPractice}
                label="Practice Name"
                required={false}
              />
            ) : (
              <>
                <label className="block text-sm font-bold theme-text-primary mb-2">Practice Name</label>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                  {gpPractice || 'Not provided'}
                </Typography>
              </>
            )}
          </div>
          <div>
            {isEditing ? (
              <PhoneField
                value={gpPhone}
                onChange={setGpPhone}
                label="GP Phone"
                required={false}
              />
            ) : (
              <>
                <label className="block text-sm font-bold theme-text-primary mb-2">Phone</label>
                {gpPhone ? (
                  <a href={`tel:${gpPhone}`} style={{ textDecoration: 'none' }}>
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500, '&:hover': { textDecoration: 'underline', color: 'rgba(255, 255, 255, 1)' } }}>
                      {gpPhone}
                    </Typography>
                  </a>
                ) : (
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                    Not provided
                  </Typography>
                )}
              </>
            )}
          </div>
          <div className="md:col-span-2">
            {isEditing ? (
              <AddressAutocomplete
                value={{
                  line1: gpAddress || '',
                  line2: gpAddressLine2 || '',
                  line3: gpAddressLine3 || '',
                  city: gpCity || '',
                  postcode: gpPostcode || '',
                  county: gpCounty || '',
                  country: gpCountry || ''
                }}
                onChange={(addressData) => {
                  setGpAddress(addressData.line1 || '');
                  setGpAddressLine2(addressData.line2 || '');
                  setGpAddressLine3(addressData.line3 || '');
                  setGpCity(addressData.city || '');
                  setGpPostcode(addressData.postcode || '');
                  setGpCounty(addressData.county || '');
                  setGpCountry(addressData.country || '');
                }}
                label="GP Practice Address"
                fieldMapping={{
                  line1: 'line1',
                  line2: 'line2',
                  line3: 'line3',
                  city: 'city',
                  postcode: 'postcode',
                  county: 'county',
                  country: 'country'
                }}
              />
            ) : (
              <>
                <label className="block text-sm font-bold theme-text-primary mb-2">Address</label>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                  {[gpAddress, gpAddressLine2, gpAddressLine3, gpCity, gpCounty, gpPostcode, gpCountry]
                    .filter(Boolean)
                    .join(', ') || 'Not provided'}
                </Typography>
              </>
            )}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInformation;
